import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { downloadFile, useFetch, fmtTime, api, formatErr } from "@/lib/api";
import { PageHeader, Loading, ErrorBox, Empty } from "@/components/common";
import { useAuth, can } from "@/context/AuthContext";
import { WeeklyReports } from "@/components/WeeklyReports";

const REPORTS = [["inventory", "Endpoint inventory", "Devices, OS, agent versions, status"], ["incidents", "Open & resolved incidents", "Severity, category, lifecycle timestamps"],
  ["remediation", "Remediation history", "Every task, risk level, outcome and initiator"], ["approvals", "Approvals", "Requests and decisions"],
  ["ai_usage", "AI usage", "Diagnostic and assistant calls with estimated cost"], ["audit", "Audit events", "Hash-chained audit trail"]];

export function Reports() {
  const { role, orgId } = useAuth();
  const [days, setDays] = useState(30);
  const dl = async (k) => {
    try { await downloadFile(`/reports/${k}.csv?days=${days}`, `codered-${k}.csv`); toast.success("Report generated"); } catch (e) { toast.error("Not permitted or failed"); }
  };
  return (
    <div className="fade-up">
      <PageHeader title="Reports" crumbs={["Portal", "Reports"]}>
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="border border-gray-300 px-3 py-2 text-sm" data-testid="reports-range-select"><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last 12 months</option></select>
      </PageHeader>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.filter(([k]) => k !== "audit" || can.audit(role)).map(([k, t, d]) => (
          <div key={k} className="card-flat p-6 lift flex flex-col">
            <h3 className="font-bold">{t}</h3><p className="text-sm text-gray-500 mt-1 flex-1">{d}</p>
            <button onClick={() => dl(k)} className="mt-4 inline-flex items-center gap-2 text-sm px-3 py-2 border border-gray-900 hover:bg-gray-900 hover:text-white self-start" data-testid={`report-download-${k}`}><Download className="w-4 h-4" />CSV</button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-6">Reports include generation timestamp and date range and only contain data from this organization.</p>
      <div className="card-flat p-6 mt-8"><h3 className="font-bold mb-1">Weekly PDF reports</h3><p className="text-sm text-gray-500 mb-4">Emailed every Monday 9:00 AM IST to your admins. Download any week to forward to management.</p>
        <WeeklyReports key={orgId} listPath="/reports/weekly" pdfBase="/reports/weekly" /></div>
    </div>
  );
}

export function Audit() {
  const { orgId } = useAuth();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useFetch(`/audit?q=${encodeURIComponent(q)}&page=${page}`, [orgId]);
  return (
    <div className="fade-up">
      <PageHeader title="Audit log" crumbs={["Portal", "Audit"]}><input placeholder="Filter action…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="border border-gray-300 px-3 py-2 text-sm" data-testid="audit-filter-input" /></PageHeader>
      <ErrorBox error={error} />
      {loading ? <Loading /> : <AuditTable data={data} page={page} setPage={setPage} />}
    </div>
  );
}

export const AuditTable = ({ data, page, setPage }) => (
  <div className="card-flat overflow-x-auto">
    <table className="data-table" data-testid="audit-table"><thead><tr><th>Seq</th><th>Time</th><th>Actor</th><th>Action</th><th>Target</th><th>IP</th><th>Hash</th></tr></thead>
      <tbody>{data.items.map((a) => <tr key={a.id}><td className="font-mono">{a.seq}</td><td className="text-xs whitespace-nowrap">{fmtTime(a.created_at)}</td><td className="text-xs">{a.actor}</td><td className="font-mono text-xs">{a.action}</td><td className="font-mono text-xs break-all">{a.target}</td><td className="text-xs">{a.ip}</td><td className="font-mono text-[10px] text-gray-400">{a.hash?.slice(0, 12)}</td></tr>)}</tbody></table>
    <div className="flex justify-between p-3 text-sm text-gray-500"><span>{data.total} events</span><div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border disabled:opacity-40">Prev</button><button disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)} className="px-3 py-1 border disabled:opacity-40" data-testid="audit-next-page">Next</button></div></div>
  </div>
);

export function MspClients() {
  const { orgId } = useAuth();
  const { data, loading, error, reload } = useFetch("/org/clients", [orgId]);
  const [name, setName] = useState("");
  const add = async () => { try { await api.post("/org/clients", { name }); setName(""); toast.success("Client organization created — reload to switch to it"); reload(); } catch (e) { toast.error(formatErr(e)); } };
  return (
    <div className="fade-up">
      <PageHeader title="MSP client organizations" crumbs={["Portal", "MSP clients"]} />
      <div className="flex gap-2 mb-4"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client organization name" className="border border-gray-300 px-3 py-2 text-sm w-72" data-testid="msp-client-name-input" /><button onClick={add} className="px-4 py-2 bg-red-600 text-white text-sm" data-testid="msp-client-create-btn">Create client</button></div>
      <ErrorBox error={error} />
      {loading ? <Loading /> : !data?.length ? <Empty title="No client organizations" /> : (
        <table className="data-table card-flat"><thead><tr><th>Name</th><th>Status</th><th>Created</th></tr></thead><tbody>{data.map((o) => <tr key={o.id}><td>{o.name}</td><td>{o.status}</td><td>{fmtTime(o.created_at)}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}
